import React, { useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import VaspAutocomplete, {
  VaspAutocompleteValue,
} from "src/components/transfers/VaspAutocomplete";
import NotabeneHostedMount from "src/components/wallets/dropdown/NotabeneHostedMount";

export type DestinationType = "Self-Hosted Wallet" | "Exchange";

/**
 * Prefix marking a `walletVerificationReference` produced by the customer's
 * self-declaration checkbox rather than a SafeConnect wallet-signature proof.
 * Kept in the same string field so no backend/schema change is needed — the
 * prefix keeps the two auditable/distinguishable.
 */
export const SELF_DECLARATION_REF_PREFIX = "self-declaration:";

export interface AddressTrKindValue {
  destinationType: DestinationType | null;
  destinationVasp?: string;
  destinationVaspName?: string;
  walletVerificationReference?: string;
}

export interface AddressTrKindProps {
  value: AddressTrKindValue;
  onChange: (next: AddressTrKindValue) => void;
  /**
   * Currency code (BTC, ETH, USDC…) of the address being added — required
   * for the self-hosted branch to launch SafeConnect's createConnectWallet.
   * Comes from the parent's selected currency; if missing the self-hosted
   * branch shows a "Pick a currency first" prompt.
   */
  asset?: string;
  /**
   * When true (withdrawal TR backfill, D4), skip ownership-proof collection
   * in this step entirely. The withdrawal-assist widget that follows collects
   * the proof itself (wallet signature OR self-declaration, per our
   * `agentSections` config) and the backend forwards it upstream via the
   * unhosted-proof relationship PATCH on transfer completion.
   *
   * Rationale: the proof popup this step would otherwise open is Notabene's
   * hosted `/connect` page, which ignores `agentSections` and offers
   * signature-only — stranding customers without live wallet access. The
   * withdrawal-assist surface honors `agentSections`, so proof collection
   * belongs there. (Verified against Notabene's deployed bundles 2026-07-20.)
   */
  deferProof?: boolean;
}

type ProofMethod = "wallet" | "declare";

/**
 * D3 step 2 — "Is this a wallet or an exchange?"
 *
 * Conditional reveals:
 *   - "Self-Hosted Wallet" → ownership-verification method chooser:
 *       - "Connect my wallet" → `<NotabeneHostedMount kind="connect-wallet" />`
 *         opens SafeConnect's modal verification flow. Resolves with a
 *         walletVerificationReference (proof.id) once the user signs.
 *       - "Self-declare" → checkbox attestation. Writes a
 *         `self-declaration:<ISO timestamp>` reference into the same field.
 *         Exists because Notabene's hosted /connect page ignores
 *         `agentSections` (signature-only), stranding users without live
 *         wallet access; the compliance-grade upstream proof is still
 *         collected by the withdrawal-assist widget at withdrawal time.
 *     With `deferProof` (withdrawal backfill), neither is shown — see the
 *     prop doc above.
 *   - "Exchange"            → `<VaspAutocomplete />` — backend-proxied
 *     directory search; final VASP binding happens later inside
 *     createWithdrawalAssist at withdrawal time. A counterparty VASP not
 *     in Notabene's directory is still resolved by SafeConnect at
 *     withdrawal time, so no free-text "Other VASP" branch is needed here.
 */
const AddressTrKind: React.FC<AddressTrKindProps> = ({
  value,
  onChange,
  asset,
  deferProof = false,
}) => {
  const isSelfDeclared = (value.walletVerificationReference || "").startsWith(
    SELF_DECLARATION_REF_PREFIX,
  );
  // Derive the initial method from an existing reference (e.g. re-entering
  // the step with state restored); afterwards the radio drives it.
  const [proofMethod, setProofMethod] = useState<ProofMethod | null>(
    value.walletVerificationReference ? (isSelfDeclared ? "declare" : "wallet") : null,
  );

  const handleKindChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value as DestinationType;
    // Clear branch-specific fields whenever the kind switches.
    setProofMethod(null);
    onChange({
      destinationType: next,
      destinationVasp: undefined,
      destinationVaspName: undefined,
      walletVerificationReference: undefined,
    });
  };

  const handleVaspPicked = (next: VaspAutocompleteValue | null) => {
    onChange({
      ...value,
      destinationVasp: next?.vaspDid,
      destinationVaspName: next?.vaspName,
    });
  };

  const handleProofMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value as ProofMethod;
    setProofMethod(next);
    // A self-declaration reference is only valid while "declare" is the
    // chosen method — switching to the wallet flow must re-require a real
    // proof. A completed wallet proof, however, survives toggling (it is a
    // stronger attestation than the checkbox and there is no way to redo it
    // without re-signing anyway).
    if (next === "wallet" && isSelfDeclared) {
      onChange({ ...value, walletVerificationReference: undefined });
    }
  };

  const handleSelfDeclareToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // Never downgrade a completed SafeConnect signature proof to the weaker
      // checkbox record (the declare branch renders a note instead of the
      // checkbox in that case — this guard is the belt to that brace).
      if (value.walletVerificationReference && !isSelfDeclared) return;
      onChange({
        ...value,
        walletVerificationReference: `${SELF_DECLARATION_REF_PREFIX}${new Date().toISOString()}`,
      });
    } else if (isSelfDeclared) {
      // Only clear references this checkbox created — never wipe a real
      // SafeConnect proof reference.
      onChange({ ...value, walletVerificationReference: undefined });
    }
  };

  const handleAttestationComplete = (data: any) => {
    // createConnectWallet resolves with `{ proof, txCreate, requestID }`.
    // The SDK's `OwnershipProof` has no `id` field — the always-present
    // identifier is `requestID` (UUID); `proof.did` is the wallet-binding
    // identifier when available. NotabeneHostedMount.emitCompletion
    // already resolves these into `walletVerificationReference`, but we
    // keep a defensive fallback here in case the caller bypasses it.
    const raw = data?.raw ?? {};
    const proof = raw?.proof ?? {};
    const proofDid = proof?.did
      ? proof?.address
        ? `${proof.did}#${proof.address}`
        : proof.did
      : "";
    const ref = String(
      data?.walletVerificationReference ||
        data?.reference ||
        proof?.id ||
        proofDid ||
        raw?.requestID ||
        "",
    );
    onChange({ ...value, walletVerificationReference: ref });
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }} data-testid="address-tr-kind-title">
        Is this a wallet or an exchange?
      </Typography>
      <FormControl fullWidth>
        <RadioGroup
          value={value.destinationType ?? ""}
          onChange={handleKindChange}
          sx={{ gap: 1 }}>
          <FormControlLabel
            value="Self-Hosted Wallet"
            control={<Radio />}
            label="Self-hosted wallet (e.g. MetaMask, Ledger)"
            data-testid="address-tr-kind-self-hosted"
          />
          <FormControlLabel
            value="Exchange"
            control={<Radio />}
            label="Exchange or financial institution"
            data-testid="address-tr-kind-exchange"
          />
        </RadioGroup>
      </FormControl>

      {value.destinationType === "Self-Hosted Wallet" && deferProof && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" data-testid="address-tr-kind-defer-proof">
            You&apos;ll verify ownership of this wallet in the next step —
            either by connecting your wallet and signing a message, or by
            declaring that the wallet is yours.
          </Typography>
        </Box>
      )}

      {value.destinationType === "Self-Hosted Wallet" && !deferProof && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            We need to verify you own this wallet. Choose how you&apos;d like
            to verify:
          </Typography>
          <FormControl fullWidth>
            <RadioGroup value={proofMethod ?? ""} onChange={handleProofMethodChange}>
              <FormControlLabel
                value="wallet"
                control={<Radio />}
                label="Connect my wallet and sign a message (recommended)"
                data-testid="address-tr-proof-method-wallet"
              />
              <FormControlLabel
                value="declare"
                control={<Radio />}
                label="I can't connect this wallet right now — declare ownership"
                data-testid="address-tr-proof-method-declare"
              />
            </RadioGroup>
          </FormControl>

          {proofMethod === "wallet" &&
            (asset ? (
              // SafeConnect's connect-wallet modal opens as soon as this
              // mounts — which is why it renders only after the customer
              // explicitly picks the wallet method, not on branch reveal.
              <NotabeneHostedMount
                kind="connect-wallet"
                asset={asset}
                onComplete={handleAttestationComplete}
              />
            ) : (
              <Typography variant="body2" color="warning.main">
                Select a currency before verifying wallet ownership.
              </Typography>
            ))}

          {proofMethod === "declare" &&
            (value.walletVerificationReference && !isSelfDeclared ? (
              // A completed wallet-signature proof outranks the checkbox —
              // don't offer a control that would replace it with a weaker
              // attestation.
              <Typography
                variant="body2"
                sx={{ mt: 1 }}
                data-testid="address-tr-declare-already-proven">
                This wallet is already verified with a signature — no
                declaration is needed.
              </Typography>
            ) : (
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelfDeclared}
                      onChange={handleSelfDeclareToggle}
                      inputProps={
                        { "data-testid": "address-tr-self-declare-checkbox" } as any
                      }
                    />
                  }
                  label="I confirm that I own and control this wallet address."
                />
              </Box>
            ))}

          {value.walletVerificationReference && (
            <Typography
              variant="subtitle2"
              color="success.main"
              sx={{ fontWeight: 600 }}
              data-testid="attestation-success">
              {isSelfDeclared ? "Ownership declared." : "Attestation complete."}
            </Typography>
          )}
        </Box>
      )}

      {value.destinationType === "Exchange" && (
        <Box sx={{ mt: 3 }}>
          <VaspAutocomplete
            value={
              value.destinationVasp
                ? {
                    vaspDid: value.destinationVasp,
                    vaspName: value.destinationVaspName ?? "",
                  }
                : null
            }
            onChange={handleVaspPicked}
          />
        </Box>
      )}
    </Box>
  );
};

export default AddressTrKind;
